import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Check } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

export const ReportDialog = ({ slug }) => {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || !email.trim()) {
      toast.error(t("report.err.fill"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/report", {
        collection_slug: slug || null,
        message: message.trim(),
        contact_email: email.trim(),
      });
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(t("report.err.failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog onOpenChange={(o) => { if (!o) { setDone(false); setMessage(""); setEmail(""); } }}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-sm text-[#7A6B63] hover:text-[#B35A3E] hover:underline"
          data-testid="report-abuse-button"
        >
          <ShieldAlert className="w-4 h-4" />
          {t("report.trigger")}
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif">{t("report.title")}</DialogTitle>
          <DialogDescription>
            {t("report.desc")}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex items-center gap-3 rounded-2xl bg-[#EAF1EA] border border-[#CBDDCB] p-4 text-[#3f6047]" data-testid="report-confirmation">
            <Check className="w-5 h-5 shrink-0" />
            <span className="text-sm">{t("report.confirmation")}</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="report-form">
            <div className="space-y-2">
              <Label htmlFor="report-message" className="text-[#2C221E]">{t("report.messageLabel")}</Label>
              <Textarea
                id="report-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("report.messagePlaceholder")}
                className="min-h-[100px] rounded-xl border-[#E8DEC2] bg-[#FDFBF7]"
                data-testid="report-message-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="report-email" className="text-[#2C221E]">{t("report.emailLabel")}</Label>
              <Input
                id="report-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("common.emailPlaceholder")}
                className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7]"
                data-testid="report-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full h-12 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
              data-testid="report-submit-button"
            >
              {sending ? t("common.sending") : t("report.submit")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
